<template>
  <div class="ce-module-toc">
    <cs-form-dialog
      v-model="popupDialog"
      heading="Save Page Visibility and Locking"
      :primary-action="{ label: 'Proceed', eventName: 'dlg-ok', disabled: loadingSaveVisibility }"
      :secondary-action1="{ label: 'Cancel', eventName: 'dlg-cancel' }"
      @primary-click="saveVisibles"
      @secondary1-click="popupDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container>
          <v-row class="flex-column">
            <p id="save-visibility-group-lock-dialog--general-warning-text">
              This page visibility and locked/unlocked status will be applied to this site
              <span
                v-if="clientChildren && clientChildren.length > 0"
                id="save-visibility-group-lock-dialog--child-sites-warning-text"
                >and all associated child sites</span
              ><span v-else id="save-visibility-group-lock-dialog--current-site-warning-text"
                >only</span
              >.
            </p>
            <p id="save-visibility-group-lock-dialog--public-warning-text" class="red-text">
              All public pages which are made invisible will no longer be publicly accessible.
            </p>
            <p>Would you like to proceed?</p>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>
    <cs-form-dialog
      v-model="showConfirmSetVisibilityPopup"
      heading="Manage Page Visibility"
      :primary-action="{ label: 'OK', eventName: 'dlg-ok' }"
      :secondary-action1="{ label: 'Cancel', eventName: 'dlg-cancel' }"
      @primary-click="setChildPageVisibilities"
      @secondary1-click="cancelPageVisibilityChanges"
    >
      <template #cs-form-dialog-content>
        <v-container>
          <v-row class="d-flex flex-column align-start justify-start">
            <p>
              Use the toggle button below to set page visibility for this page only or this page and
              all child pages.
            </p>
            <v-switch
              :id="`secure-dialog--toggle-secure-module-${moduleId}`"
              v-model="applyVisibilityToChildPages"
              :disabled="visibilityDirection === 1"
              label="Apply to child pages"
            />
            <p id="visibility-dialog--general-warning-text" class="text-error">
              Click OK to set visibility for this page<span
                v-if="applyVisibilityToChildPages"
                id="visibility-dialog--child-pages-warning-text"
              >
                and all child pages</span
              ><span v-else id="visibility-dialog--current-page-warning-text"> only</span>.
            </p>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>
    <v-lazy v-if="isExpanded" :min-height="39">
      <v-treeview
        ref="tocTree"
        v-model:selected="selected"
        :items="debouncedItems"
        :load-children="loadChildren"
        :opened="open"
        :activated="active"
        :selectable="isAdminClient ? showVisibilityToggle : false"
        open-on-click
        item-title="name"
        item-value="vpath"
        activatable
        tile
        density="compact"
        class="text-left"
        active-class="imsuperselected"
        active-strategy="single-independent"
        select-strategy="independent"
        selected-color="rgb(var(--v-theme-secondary))"
        true-icon="mdi-toggle-switch"
        false-icon="mdi-toggle-switch-off"
        indeterminate-icon="mdi-toggle-switch"
        expand-icon="mdi-menu-right"
        collapse-icon="mdi-menu-down"
        @update:activated="onUpdateActivated"
        @update:selected="onSwitched"
        @update:opened="onUpdateOpened"
      >
        <template #title="{ item }">
          <div
            v-if="isAdminClient"
            class="d-flex flex-row align-center justify-space-between page-status"
          >
            <div
              :id="computedPageStatus[item.path]?.nodeId"
              class="text-link text-truncate"
              :title="item ? item.name : ''"
            >
              <div class="text-truncate list-item-title">
                {{ item ? item.name : '' }}
              </div>
            </div>
            <div class="pl-3 pr-5">
              <v-tooltip
                v-if="computedPageStatus[item.path]?.isShowGroupLock"
                :eager="false"
                location="right"
              >
                <template #activator="{ props }">
                  <v-icon
                    v-if="computedPageStatus[item.path]?.isGroupLockedByClient && isGroupAdminSite"
                    v-bind="props"
                    :id="`c-e-module-toc-client-groupLock-icon-${item.path.slice(0, -3)}`"
                    @click.stop="groupLockToggle(item.path)"
                  >
                    mdi-lock-outline
                  </v-icon>
                  <v-icon
                    v-else-if="computedPageStatus[item.path]?.isGroupLocked"
                    v-bind="props"
                    :id="`c-e-module-toc--groupLock-icon-${item.path.slice(0, -3)}`"
                    @click.stop="groupLockToggle(item.path)"
                  >
                    mdi-lock
                  </v-icon>
                  <v-icon
                    v-else-if="isGroupAdminSite"
                    v-bind="props"
                    :id="`c-e-module-toc--groupLock-open-icon-${item.path.slice(0, -3)}`"
                    @click.stop="groupLockToggle(item.path)"
                  >
                    mdi-lock-open-variant-outline
                  </v-icon>
                </template>
                <span>{{ computedPageStatus[item.path]?.tooltip }}</span>
              </v-tooltip>
              <v-tooltip
                v-if="computedPageStatus[item.path]?.isUGCPage"
                :eager="false"
                location="right"
              >
                <template #activator="{ props }">
                  <v-icon
                    v-if="computedPageStatus[item.path]?.isPageOwner"
                    v-bind="props"
                    :id="`c-e-module-t-o-c--person-icon-${item.path.slice(0, -3)}`"
                  >
                    mdi-account
                  </v-icon>
                  <v-icon
                    v-else
                    v-bind="props"
                    :id="`c-e-module-t-o-c--group-icon-${item.path.slice(0, -3)}`"
                  >
                    mdi-account-group
                  </v-icon>
                </template>
                <span v-if="computedPageStatus[item.path]?.isPageOwner">
                  This is your page, you can edit this.
                </span>
                <span v-else>This page is owned by your Group Administrator.</span>
              </v-tooltip>
            </div>
          </div>

          <div
            v-else
            :id="computedPageStatus[item.path]?.nodeId"
            class="text-link text-truncate"
            :title="item ? item.name : ''"
          >
            {{ item ? item.name : '' }}
          </div>
        </template>

        <template v-if="isAdminClient" #append="{ item }">
          <div v-if="computedPageStatus[item.path]?.isEditable" class="page-btns">
            <v-tooltip :eager="false" location="right">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  :id="`c-e-module-t-o-c--add-page-btn-${item.path.slice(0, -3)}`"
                  icon="mdi-plus"
                  variant="plain"
                  tile
                  density="compact"
                  @click.stop="addPage({ item, moduleId })"
                >
                </v-btn>
              </template>
              <span>Create a child page</span>
            </v-tooltip>
          </div>
        </template>
      </v-treeview>
    </v-lazy>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { CSBase, CSFormDialog } from '@complispace/cs-design-system';
import EventBus from '@/bus';
import { content } from '@/api';
import * as types from '@/store/mutationTypes';
import {
  TOC_VERSION_2,
  UPDATE_CMD,
  PAGE_CHANGE_CMD,
  COMPONENT_STATE,
  COMPONENT_MANAGER_TYPE,
  EVENTBUS_CMD,
  VIEW_TYPES,
  MESSAGES,
  ROLES,
  MIXPANEL_TRACE_EVENT,
  MIXPANEL_TRACE_STRING
} from '@/constants';
import routerUtils from '@/lib/routerUtils';
import treeUtils from '@/lib/treeutils';
import { plugInAdapter } from '@/lib/globalPlugIns';
import { debounce } from 'lodash';

const CE_MODULE_TYPES = {
  TYPE_COMPLISPACE: 1,
  TYPE_CLIENT: 2
};

const SELECTION_DIR = {
  DIR_TURNING_OFF: 1,
  DIR_TURNING_ON: 2
};

export default {
  name: 'CEModuleTOC',

  components: {
    CSFormDialog
  },

  extends: CSBase,

  props: {
    moduleId: Number,
    viewType: Number,
    isOwnedByClient: Boolean,
    tocVersion: Number,
    isExpanded: Boolean
  },

  emits: ['pageSelected', 'pageChanges', 'changeShowSaveBtn', 'setState'],

  data() {
    return {
      CE_MODULE_TYPES,
      loading: false,
      active: [],
      open: [],
      selected: [],
      popupDialog: false,
      componentState: COMPONENT_STATE.STATE_IDLE,
      currentPath: '',
      items: [],
      debouncedItems: [], // truncated tree without children passed to the treeview
      initialPathMap: {},
      lastSelection: null,
      newVisiblePages: null,
      lastActive: [],
      groupLockedPages: [],
      lastGroupLockedPages: null,
      showConfirmSetVisibilityPopup: false,
      visibilityDirection: 1,
      applyVisibilityToChildPages: false,
      loadingSaveVisibility: false,
      maxDepth: 0
    };
  },

  watch: {
    items: {
      handler(newVal) {
        this.debouncedUpdateItems(newVal);
      },
      deep: true
    }
  },

  created() {
    this.debouncedUpdateItems = debounce(this.updateItems, 200);
  },

  computed: {
    ...mapState({
      navigation: (state) => state.navigation.navigation,
      clientChildren: (state) => state.client.children,
      clientId: (state) => state.client.id,
      client: (state) => state.client,
      subdomain: (state) => state.session.subdomain
    }),

    ...mapGetters({
      isAllowedTo: 'authorisation/isAllowedTo',
      hasAccess: 'authorisation/hasAccess',
      isAdminDraftView: 'authorisation/isAdminDraftView',
      getValidPageFromNav: 'navigation/getValidPageFromNav',
      isSingleSite: 'client/isSingleSite',
      role: 'authorisation/role',
      getMasterTOC: 'data/getMasterTOC',
      contextItemValues: 'clientContext/values',
      activatedCEModules: 'activatedCEModules/items',
      appName: 'app/name'
    }),

    showVisibilityToggle() {
      return !this.isAdminDraftView;
    },

    isGroupAdminSite() {
      return (
        this.clientChildren &&
        this.clientChildren.length > 0 &&
        (this.role === ROLES.GROUP_ADMIN || this.role === ROLES.CS_ADMIN)
      );
    },

    isAdminClient() {
      return this.hasAccess('onoffaccessTable') || this.hasAccess('onoffmessageaccessTable');
    },

    computedPageStatus() {
      const statusMap = {};

      function traverse(node) {
        if (!node) return;

        statusMap[node.path] = {
          isShowGroupLock: this.isShowGroupLock(node.path),
          isGroupLocked: this.isGroupLocked(node.path),
          isGroupLockedByClient: this.isGroupLockedByClient(node.path),
          isPageOwner: this.isPageOwner(node),
          isUGCPage: this.isUGCPage(node),
          isEditable: this.isEditable(node.level),
          nodeId: this.getNodeId(this.moduleId, node),
          tooltip: this.getGroupLockTooltip(node.path)
        };

        if (node.children && node.children.length > 0) {
          node.children.forEach((child) => traverse.call(this, child));
        }
      }

      this.items.forEach((rootNode) => traverse.call(this, rootNode));
      return statusMap;
    }
  },

  mounted() {
    this.componentState = COMPONENT_STATE.STATE_READY_FOR_DATA;

    this.messageParentComponent('setState', {
      id: this.moduleId,
      type: COMPONENT_MANAGER_TYPE.TYPE_COM_STATE,
      value: COMPONENT_STATE.STATE_READY_FOR_DATA
    });
  },

  methods: {
    ...mapMutations({
      setInvisiblePages: types.PAGE_VISIBILITIES_SET_INVISIBLE_PAGES
    }),

    updateItems(newItems) {
      const excludeTruncate = this.open;
      this.debouncedItems = newItems.map((item) =>
        treeUtils.truncateTreeDepth(item, this.maxDepth, excludeTruncate)
      );
    },

    onUpdateActivated(active) {
      this.active = active;
      this.updateActive();
    },

    onUpdateOpened(newOpenNodes) {
      const oldOpenNodes = this.open;
      const activeNode = this.active[0];

      const openingNode = newOpenNodes.find((path) => !oldOpenNodes.includes(path));
      const closingNode = oldOpenNodes.find((path) => !newOpenNodes.includes(path));

      if (openingNode && openingNode !== activeNode) {
        this.onUpdateActivated([openingNode]);
      } else if (closingNode && closingNode !== activeNode) {
        this.onUpdateActivated([closingNode]);
      } else {
        this.open = newOpenNodes;
      }
    },

    isAdmin() {
      const roleId = parseInt(this.role, 10);
      if (roleId === 4 || roleId === 3 || roleId === 2) {
        return true;
      }
      return false;
    },

    getActivatedCEModule(moduleId) {
      if (this.activatedCEModules && this.activatedCEModules.length) {
        return this.activatedCEModules.find((module) => module.id === moduleId);
      }
      return null;
    },

    isGroupLockedByClient(itemPath) {
      const pages = treeUtils.treeToList(this.items[0]);
      const pageWithProperties = pages.find((page) => page.path === itemPath);
      if (pageWithProperties && pageWithProperties.groupLocked) {
        return (
          this.groupLockedPages.findIndex((page) => page === itemPath) !== -1 &&
          pageWithProperties.lockOwnerId === this.clientId
        );
      }
      return this.groupLockedPages.findIndex((page) => page === itemPath) !== -1;
    },

    isGroupLocked(itemPath) {
      return this.groupLockedPages.findIndex((page) => page === itemPath) !== -1;
    },

    losingFocus() {
      this.active = [];
      this.currentPath = '';
      this.open = [];
    },

    isEditable(itemLevel) {
      let ret = false;

      if (this.isAdminDraftView) {
        if (itemLevel < 5) {
          if (this.isOwnedByClient || this.tocVersion === TOC_VERSION_2) {
            ret = true;
          }
        }
      }

      return ret;
    },

    getSelectionFromPageMap(pageMap, prop) {
      const res = [];
      Object.keys(pageMap).forEach((key) => {
        if (
          pageMap[key] &&
          Object.prototype.hasOwnProperty.call(pageMap[key], prop) &&
          pageMap[key][prop]
        ) {
          res.push(key);
        }
      });
      return res;
    },

    getVisibilityFromItem(toc) {
      let res = {};

      // this.$set(res, toc.path, { selected: toc.selected, groupLocked: toc.groupLocked });
      res[toc.path] = { selected: toc.selected, groupLocked: toc.groupLocked };

      if (toc.children && toc.children.length > 0) {
        toc.children.forEach((ele) => {
          const children = this.getVisibilityFromItem(ele);
          res = Object.assign(res, children);
        });
      }

      return res;
    },

    intersect(arr1, arr2) {
      if (arr1 && arr2) {
        return arr1.filter((x) => arr2.findIndex((ele) => ele === x) !== -1);
      }
      return [];
    },

    minus(arr1, arr2) {
      if (arr1 && arr2) {
        if (arr1.length > 0) {
          return arr1.filter((x) => arr2.findIndex((ele) => ele === x) === -1);
        }
        if (arr2.length > 0) {
          return arr2.filter((x) => arr1.findIndex((ele) => ele === x) === -1);
        }
        return [];
      }
      return [];
    },

    turnOnParentPages(page, toc) {
      let parent = [];
      let find = false;
      const tar = [page];

      if (toc && toc.children && toc.children.length > 0) {
        find = this.checkLogicFromChild(tar, toc.children);

        if (find) {
          parent.push(toc.path);

          if (toc.children.findIndex((ele) => ele.path === page) === -1) {
            toc.children.forEach((child) => {
              parent = parent.concat(this.turnOnParentPages(page, child));
            });
          }
        }
      }

      return parent;
    },

    getTOCFromPage(page, toc) {
      let res = null;
      if (toc && toc.path === page) {
        res = toc;
      } else if (toc && toc.children && toc.children.length > 0) {
        toc.children.some((child) => {
          res = this.getTOCFromPage(page, child);
          if (res !== null) {
            return true;
          }
          return false;
        });
      }

      return res;
    },

    turnOnChildPages(selection, page, toc) {
      let res = selection;

      const tar = this.getTOCFromPage(page, toc);

      if (tar && tar.children && tar.children.length > 0) {
        tar.children.forEach((child) => {
          const idx = res.findIndex((ele) => ele === child.path);

          if (idx === -1) {
            res.push(child.path);
          }

          res = this.turnOnChildPages(res, child.path, child);
        });
      }

      return res;
    },

    turnOffChildPages(selection, page, toc) {
      let res = selection;

      const tar = this.getTOCFromPage(page, toc);

      if (tar && tar.children && tar.children.length > 0) {
        tar.children.forEach((child) => {
          const idx = res.findIndex((ele) => ele === child.path);
          if (idx !== -1) {
            res.splice(idx, 1);
          }

          res = this.turnOffChildPages(res, child.path, child);
        });
      }

      return res;
    },

    amendLogicForSelection(cal, src, toc, dir, mode) {
      let res = [];
      let minus = null;

      if (dir === SELECTION_DIR.DIR_TURNING_ON) {
        minus = this.minus(cal, src);

        minus.forEach((ele) => {
          const turnOnParent = this.turnOnParentPages(ele, toc);
          if (turnOnParent && turnOnParent.length > 0) {
            res = res.concat(turnOnParent);
          }
        });
        const siblings = this.minus(cal, res);
        res = res.concat(siblings);
      } else if (dir === SELECTION_DIR.DIR_TURNING_OFF) {
        minus = this.minus(src, cal);
        res = res.concat(cal);

        if (mode === 'Group Lock') {
          minus.forEach((ele) => {
            res = this.turnOffChildPages(res, ele, toc);
          });
        }
      }

      return res;
    },

    getSelectionDir(newSelection, oldSelection) {
      let res = null;

      if ((newSelection?.length || 0) > (oldSelection?.length || 0)) {
        res = SELECTION_DIR.DIR_TURNING_ON;
      } else if (newSelection.length < oldSelection.length) {
        res = SELECTION_DIR.DIR_TURNING_OFF;
      }
      return res;
    },

    checkVisibilityChange(newSelection, oldSelection) {
      if ((newSelection?.length || 0) < (oldSelection?.length || 0)) {
        const closingPages = this.minus(oldSelection, newSelection);
        const pagesWithProperties = treeUtils.treeToList(this.items[0]);
        const closingPageProperties = pagesWithProperties.find(
          (page) => page.path === closingPages[0]
        );
        if (
          closingPageProperties &&
          closingPageProperties.groupLocked === true &&
          closingPageProperties.lockOwnerId !== this.clientId
        ) {
          return false;
        }
      }

      return true;
    },

    lockedAlert() {
      window.alert('This page has been locked by your Group Administrator.');
      this.resetSelection();
    },

    onSwitched() {
      if (this.componentState === COMPONENT_STATE.STATE_RECEIVING_DATA) {
        this.componentState = COMPONENT_STATE.STATE_IDLE;
      }

      if (this.isAdminClient && this.viewType === VIEW_TYPES.ADMIN_PUBLISH) {
        const dir = this.getSelectionDir(this.selected, this.lastSelection);

        if (dir) {
          let toc;
          this.visibilityDirection = dir;
          const canBeChanged = this.checkVisibilityChange(this.selected, this.lastSelection);

          if (canBeChanged) {
            if (dir === SELECTION_DIR.DIR_TURNING_ON) {
              this.newVisiblePages = this.minus(this.selected, this.lastSelection);
              toc = this.getTOCFromPage(this.newVisiblePages[0], this.items[0]);
            }

            if (dir === SELECTION_DIR.DIR_TURNING_OFF) {
              this.newInvisiblePages = this.minus(this.lastSelection, this.selected);
              toc = this.getTOCFromPage(this.newInvisiblePages[0], this.items[0]);
            }

            if (toc && toc.children && toc.children.length > 0) {
              if (dir === SELECTION_DIR.DIR_TURNING_OFF) {
                this.applyVisibilityToChildPages = true;
              }
              this.showConfirmSetVisibilityPopup = true;
            } else {
              this.selected = this.amendLogicForSelection(
                this.selected,
                this.lastSelection,
                this.items[0],
                dir,
                'Visibility'
              );
              this.lastSelection = this.selected;
            }
          } else {
            this.lockedAlert();
          }
        }

        this.checkSelected();
      }
    },

    getNodeId(moduleId, item) {
      const path = item && item.path ? item.path.substring(0, item.path.length - 3) : '';
      return `toc-node-${moduleId}-${path}`;
    },

    getAllLeafChildren(tocPage, list) {
      if (tocPage.children && tocPage.children.length > 0) {
        tocPage.children.forEach((child) => {
          if (child.children && child.children.length > 0) {
            this.getAllLeafChildren(child, list);
          } else {
            list.push(child);
          }
        });
      }
    },

    async addPage(args) {
      if (this.isAllowedTo(4) || this.isAllowedTo(3) || this.isAllowedTo(2)) {
        this.setLoading(true);

        try {
          const { item, moduleId } = args;
          const { path } = item;

          // PC-353: Temporary implementation until editor direction finalised
          moment.tz.setDefault('Australia/Sydney');
          const currentDate = moment(new Date()).format('YYYY-MM-DD HH:mm:ss');
          const pageTitle = `Page: ${currentDate}`;
          const pageContent = 'This is a blank page and is ready for you to add your content.';

          // this.componentState = COMPONENT_STATE.STATE_ADDING_PAGES;
          const response = await content.createPage(moduleId, path, pageTitle, pageContent);

          if (response.status === 201) {
            // we do not setLoading to false, but indicate onNewPageAdded method call
            // to bypassLoadingValidation
            this.currentPath = response.data.path;
            this.$emit('pageChanges', {
              id: moduleId,
              cmd: PAGE_CHANGE_CMD.UPDATE_DATA,
              values: {
                reload: true,
                unPublish: true
              }
            });
          }
        } catch (err) {
          if (err.response && err.response.status === 404) {
            this.showErrorAlert(err.response.data.message);
          } else {
            this.showErrorAlert(err.message);
          }
          console.error(err);
          this.componentState = COMPONENT_STATE.STATE_IDLE;
          this.setLoading(false);
        }
      }
    },

    async saveVisibles() {
      if (this.isAllowedTo(4) || this.isAllowedTo(3) || this.isAllowedTo(2)) {
        try {
          this.loadingSaveVisibility = true;

          const masterTOC = this.getMasterTOC(this.moduleId, this.contextItemValues, this.viewType);
          const pagesOnMasterTOC = treeUtils.treeToList(masterTOC);
          let previousMasterTocList = [];
          const previousResponse = await content.getToc(this.moduleId, 'master');

          if (previousResponse && previousResponse.status === 200 && previousResponse.data) {
            previousMasterTocList = treeUtils.treeToList(previousResponse.data);
          }

          const pagesOnComponent = treeUtils.treeToList(this.items[0]);

          const invisiblePages = this.minus(
            pagesOnMasterTOC.map((page) => page.path),
            pagesOnComponent.map((page) => page.path)
          );

          const pagesWithProperties = pagesOnMasterTOC.map((page) => {
            let isVisible = page.visible;
            let isGroupLocked = page.groupLocked;

            if (invisiblePages.findIndex((invisiblePage) => invisiblePage === page.path) === -1) {
              isVisible =
                this.selected.findIndex((visiblePage) => visiblePage === page.path) !== -1;
              isGroupLocked =
                this.groupLockedPages.findIndex(
                  (groupLockedPage) => groupLockedPage === page.path
                ) !== -1;
            }

            return {
              path: page.path,
              visible: isVisible,
              groupLocked: isGroupLocked
            };
          });

          const result = await content.setPageProperties(this.moduleId, pagesWithProperties);
          if (result.status === 200) {
            this.messageParentComponent('pageChanges', {
              id: this.moduleId,
              cmd: PAGE_CHANGE_CMD.UPDATE_DATA,
              values: {
                reload: true
              }
            });
          }

          // Track Page Visibility events in Mixpanel.
          this.trackPageVisibilityEvent(previousMasterTocList, pagesWithProperties);
        } catch (err) {
          if (err.response && err.response.status === 404) {
            this.showErrorAlert(err.response.data.message);
          } else {
            this.showErrorAlert(err.message);
          }
        } finally {
          this.popupDialog = false;
          this.loadingSaveVisibility = false;
        }
      }
    },

    trackPageVisibilityEvent(previousPageList, updatedPageList) {
      try {
        // Find list of pages for which visibility was changed.
        const visibilityUpdatedPages = updatedPageList.reduce((acc, updatedPage) => {
          const page = previousPageList.find(
            (previousPage) => previousPage.path === updatedPage.path
          );

          if (page.visible !== updatedPage.visible)
            acc.push({
              pagePath: updatedPage.path,
              pageTitle: page.name,
              pageOwnerId: page.creatorId,
              visible: updatedPage.visible
            });

          return acc;
        }, []);

        const { plugIns } = this.$app;
        const userRole = this.isAdmin()
          ? MIXPANEL_TRACE_STRING.USER_ROLE_VIEW.ADMIN
          : MIXPANEL_TRACE_STRING.USER_ROLE_VIEW.CLIENT;
        const currentModule = this.getActivatedCEModule(this.moduleId);
        let moduleOwnerId;
        let moduleName;
        if (currentModule) {
          moduleOwnerId = currentModule.clientId;
          moduleName = currentModule.displayName;
        }

        const commonMixpanelProperties = {
          appName: this.appName,
          client: this.client,
          subdomain: this.subdomain,
          userRole,
          moduleId: this.moduleId,
          moduleOwnerId,
          moduleName
        };

        visibilityUpdatedPages.forEach((page) => {
          const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({
            ...commonMixpanelProperties,
            pagePath: page.pagePath,
            pageOwnerId: page.pageOwnerId,
            pageTitle: page.pageTitle
          });
          const visibilityEvent = page.visible
            ? MIXPANEL_TRACE_EVENT.PAGE_VISIBILITY_ON
            : MIXPANEL_TRACE_EVENT.PAGE_VISIBILITY_OFF;

          plugIns.mixPanelTrackEvent(visibilityEvent, properties);
        });
      } catch (error) {
        console.log('Mixpanel Error: ', error);
      }
    },

    async saveConfirm() {
      this.popupDialog = true;
    },

    checkLogicFromChild(selection, tocs) {
      let find = false;

      tocs.some((toc) => {
        if (selection.findIndex((path) => toc.path === path) !== -1) {
          find = true;
          return true;
        }
        return false;
      });

      if (!find) {
        tocs.some((toc) => {
          if (toc.children && toc.children.length > 0) {
            find = this.checkLogicFromChild(selection, toc.children);
            if (find) {
              return true;
            }
          }
          return false;
        });
      }

      return find;
    },

    isEqual(array1, array2) {
      const arr1 = array1.sort().join('');
      const arr2 = array2.sort().join('');
      return arr1 === arr2;
    },

    isPageOwner(toc) {
      return toc.creatorId === this.clientId;
    },

    isUGCPage(toc) {
      if (toc.creatorId) {
        return true;
      }
      return false;
    },

    checkSelected() {
      const initialSelection = this.getSelectionFromPageMap(this.initialPathMap, 'selected');
      const initialGroupLocked = this.getSelectionFromPageMap(this.initialPathMap, 'groupLocked');

      if (
        !this.isEqual(initialSelection, this.selected) ||
        !this.isEqual(initialGroupLocked, this.groupLockedPages)
      ) {
        this.messageParentComponent('changeShowSaveBtn', {
          moduleId: this.moduleId,
          value: true
        });
      } else {
        this.messageParentComponent('changeShowSaveBtn', {
          moduleId: this.moduleId,
          value: false
        });
      }
    },

    openForActive(lineage) {
      lineage.forEach((l) => {
        if (!this.open.includes(l)) {
          this.open.push(l);
        }
      });
    },

    closeForNonActiveBranch(active) {
      if (this.checkPageInNav(this.moduleId, active)) {
        const { lineage } = this.navigation[this.moduleId][active];
        let parents = lineage.map((item) => item.path);
        parents = parents.filter((item) => item !== active);
        this.open = this.intersect(parents, this.open);
      }
    },

    setActiveItem(activePath, lineage) {
      this.active = [activePath];
      this.updateActive(); // this will update the router
      this.openForActive(lineage);
    },

    hardRefreshPage() {
      EventBus.$emit(EVENTBUS_CMD.HARD_REFRESH, this.moduleId, this.currentPath);
    },

    updateTree(moduleId, pagePath, hardRefresh) {
      const { lineage } = this.navigation[moduleId][pagePath];
      const active = this.active[0];
      this.setActiveItem(
        pagePath,
        lineage.map((item) => item.path)
      );

      if (hardRefresh && active === this.active[0]) {
        this.hardRefreshPage();
      }
    },

    initExpandOneLevel() {
      if (this.items && this.items.length > 0) {
        this.getOneLevel(this.items);
      }
    },

    getOneLevel(toc) {
      toc.forEach((t) => {
        if (t) {
          if (t.level === 0) {
            this.open.push(t.path);
          }
        }
      });
    },

    updateRouter() {
      if (this.active.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        this.currentPath = this.active[0];

        this.$router
          .push({
            name: 'Page',
            params: {
              moduleId: this.moduleId,
              pagePath: this.active[0]
            }
          })
          .catch(routerUtils.handleNavigationFailures);
      } else if (this.$router.currentRoute.name === 'Page') {
        this.$router.push('/').catch(routerUtils.handleNavigationFailures);
      }
    },

    messageParentComponent(cmd, msg) {
      this.$emit(cmd, msg);
    },

    updateActive() {
      if (this.active.length) {
        const { lineage } = this.navigation[this.moduleId][this.active[0]];

        if (this.open.findIndex((openitem) => openitem === this.active[0]) === -1) {
          this.openForActive(lineage.map((item) => item.path));
          this.closeForNonActiveBranch(this.active[0]);
          this.open.push(this.active[0]);
        }

        this.updateRouter();
        this.messageParentComponent('pageSelected', {
          moduleId: this.moduleId,
          path: this.active[0]
        });
      } else if (this.open.findIndex((openitem) => openitem === this.lastActive[0]) !== -1) {
        this.closeForNonActiveBranch(this.lastActive[0]);
      }
      this.lastActive = this.active;
    },

    checkPageInNav(moduleId, path) {
      let res = false;
      if (
        !Object.prototype.hasOwnProperty.call(this.navigation, moduleId) ||
        !this.navigation[moduleId]
      ) {
        res = false;
      } else if (
        !Object.prototype.hasOwnProperty.call(this.navigation[moduleId], path) ||
        !this.navigation[moduleId][path]
      ) {
        res = false;
      } else {
        res = true;
      }

      return res;
    },

    getNextPage(moduleId, curPath, direction) {
      let res = null;

      if (this.checkPageInNav(moduleId, curPath)) {
        if (direction === UPDATE_CMD.PAGEUP) {
          res = this.navigation[moduleId][curPath].previous;
        } else if (direction === UPDATE_CMD.PAGEDOWN) {
          res = this.navigation[moduleId][curPath].next;
        }

        if (res === '') {
          res = null;
        }
      }

      return res;
    },

    updateActiveFromRoute() {
      const { moduleId, pagePath } = this.$route.params;

      if (moduleId !== undefined && pagePath !== undefined) {
        if (parseInt(moduleId, 10) !== this.moduleId) return;
        if (!this.navigation[moduleId][pagePath]) return;

        const { lineage } = this.navigation[moduleId][pagePath];
        if (!lineage) return;

        this.setActiveItem(
          pagePath,
          lineage.map((item) => item.path)
        );
      }
    },

    resetSelection() {
      this.initialPathMap = this.getVisibilityFromItem(this.items[0]);
      this.selected = this.getSelectionFromPageMap(this.initialPathMap, 'selected');
      this.groupLockedPages = this.getSelectionFromPageMap(this.initialPathMap, 'groupLocked');
      this.lastSelection = this.selected;
      this.lastGroupLockedPages = this.groupLockedPages.slice();
    },

    getUpdateFromParent(cmd, values) {
      let hardRefresh = false;

      switch (cmd) {
        case UPDATE_CMD.PAGEDOWN:
        case UPDATE_CMD.PAGEUP:
          {
            // getNextPage is about to get the next page. When cmd is pagedown, it will get the next page. When cmd is pageup, it will get the previous page.
            const nextpage = this.getNextPage(this.moduleId, this.currentPath, cmd);
            if (nextpage) {
              this.currentPath = nextpage;
              this.updateTree(this.moduleId, this.currentPath, hardRefresh);
            }
          }
          break;

        case UPDATE_CMD.LOSEFOCUS:
          this.losingFocus();
          break;

        case UPDATE_CMD.REFRESH:
          this.resetSelection();
          break;

        case UPDATE_CMD.DATACHANGE:
          {
            const pValue = JSON.parse(JSON.stringify(values.items));
            this.items = [];
            this.items.push(pValue);
            this.resetSelection();

            if (this.componentState === COMPONENT_STATE.STATE_READY_FOR_DATA) {
              this.initExpandOneLevel();
              this.updateActiveFromRoute();
              this.componentState = COMPONENT_STATE.STATE_IDLE;
            } else {
              this.componentState = COMPONENT_STATE.STATE_RECEIVING_DATA;
              hardRefresh = true;
            }
            if (Object.prototype.hasOwnProperty.call(values, 'path') && values.path) {
              this.currentPath = values.path;
            }

            // Check if the page exists in the navigation and belongs to the current active module.
            if (
              this.checkPageInNav(this.moduleId, this.currentPath) &&
              (!values.moduleId || this.moduleId === values.moduleId)
            ) {
              this.updateTree(this.moduleId, this.currentPath, hardRefresh);
            }
          }
          break;

        case UPDATE_CMD.SEARCH:
          if (Object.prototype.hasOwnProperty.call(values, 'path') && values.path) {
            this.currentPath = values.path;
          }
          if (this.checkPageInNav(this.moduleId, this.currentPath)) {
            this.updateTree(this.moduleId, this.currentPath, hardRefresh);
          }
          break;

        case UPDATE_CMD.SHOWINGERROR:
          this.showErrorAlert(MESSAGES.ERROR.NOT_ACCESSIBLE);
          break;

        default:
          break;
      }
    },

    async groupLockToggle(itemPath) {
      const pages = treeUtils.treeToList(this.items[0]);
      const pageWithProperties = pages.find((page) => page.path === itemPath);
      if (
        !pageWithProperties ||
        (pageWithProperties.groupLocked && pageWithProperties.lockOwnerId !== this.clientId)
      ) {
        return;
      }

      const index = this.groupLockedPages.findIndex((page) => page === itemPath);
      let direction = null;

      if (index === -1) {
        direction = SELECTION_DIR.DIR_TURNING_ON;
        this.groupLockedPages.push(itemPath);
      } else {
        direction = SELECTION_DIR.DIR_TURNING_OFF;
        this.groupLockedPages.splice(index, 1);
      }

      if (direction) {
        this.groupLockedPages = this.amendLogicForSelection(
          this.groupLockedPages,
          this.lastGroupLockedPages,
          this.items[0],
          direction,
          'Group Lock'
        );
      }

      this.checkSelected();
      this.lastGroupLockedPages = this.groupLockedPages.slice();
    },

    isShowGroupLock(itemPath) {
      return (
        this.viewType === VIEW_TYPES.ADMIN_PUBLISH &&
        this.selected.findIndex((page) => page === itemPath) !== -1 &&
        !this.isSingleSite
      );
    },

    getGroupLockTooltip(itemPath) {
      const pages = treeUtils.treeToList(this.items[0]);
      const pageWithProperties = pages.find((page) => page.path === itemPath);
      let tooltip = '';

      if (!pageWithProperties) return '';

      if (this.groupLockedPages.find((page) => page === itemPath)) {
        if (
          pageWithProperties.lockOwnerId === this.clientId ||
          (pageWithProperties.lockOwnerId === null && !pageWithProperties.groupLocked)
        ) {
          tooltip = 'Click to unlock this page and all page levels below.';
        } else {
          tooltip = 'This page visibility has been locked by your Group Administrator.';
        }
      } else {
        tooltip = 'Click to lock this page, please note this will lock all page levels above.';
      }

      return tooltip;
    },

    setChildPageVisibilities() {
      this.selected = this.amendLogicForSelection(
        this.selected,
        this.lastSelection,
        this.items[0],
        this.visibilityDirection,
        'Visibility'
      );

      if (this.applyVisibilityToChildPages) {
        if (this.visibilityDirection === SELECTION_DIR.DIR_TURNING_ON) {
          this.selected = this.turnOnChildPages(
            this.selected,
            this.newVisiblePages[0],
            this.items[0]
          );
        }

        if (this.visibilityDirection === SELECTION_DIR.DIR_TURNING_OFF) {
          this.selected = this.turnOffChildPages(
            this.selected,
            this.newInvisiblePages[0],
            this.items[0]
          );
        }
      }

      this.lastSelection = this.selected;
      this.showConfirmSetVisibilityPopup = false;
      this.checkSelected();
    },

    cancelPageVisibilityChanges() {
      this.selected = this.lastSelection;
      this.showConfirmSetVisibilityPopup = false;
      this.checkSelected();
    },

    getChildrenByPath(vpath) {
      // Find the full node in items
      const fullNode = treeUtils.findNodeByPath(this.items, vpath);

      if (!fullNode?.children?.length) {
        return []; // No children found
      }
      // For each child, truncate its depth to limit the number of levels loaded at once
      const excludeTruncate = this.open;
      const truncatedChildren = fullNode.children.map((child) =>
        treeUtils.truncateTreeDepth(child, this.maxDepth, excludeTruncate)
      );
      return truncatedChildren;
    },

    async loadChildren(node) {
      const { vpath } = node;
      if (node.children.length === 0) {
        const nodeChildren = this.getChildrenByPath(vpath);
        node.children.push(...nodeChildren);
      }
      return node;
    }
  }
};
</script>

<style scoped>
.page-btns .v-btn:hover,
.page-btns .v-btn:focus,
.page-btns .v-icon:hover,
.page-btns .v-icon:focus,
.page-status .v-icon {
  color: rgba(0, 0, 0, 0.54) !important;
}

.ce-module-toc :deep(.v-list-item) {
  border-top: 0.5px solid rgba(0, 0, 0, 0.12);
  padding-right: 9px;
  padding-left: 10px;
}

.ce-module-toc :deep(.list-item-title) {
  line-height: 2.25rem;
}

.ce-module-toc .list-item-title {
  line-height: 2rem;
}

/* active */
.ce-module-toc :deep(.v-list-item--active > .v-list-item__overlay) {
  background-color: #f4f5f6;
  opacity: 0;
}

/* made v-icon visible clearly  */
.ce-module-toc :deep(.v-selection-control__input > .mdi-toggle-switch-off.v-icon) {
  opacity: 1;
}
</style>

<style>
#nav-container .v-expansion-panel-text__wrapper {
  padding: 0px;
}

.ce-module-toc .v-list {
  padding: 0px;
}

#nav-container .v-expansion-panels {
  padding-right: 1px;
}

.v-treeview-node--active {
  background-color: transparent !important;
}

/** checkbox for treeview vuetify3 **/
.ce-module-toc .v-list-item__prepend div:has(.v-checkbox-btn) {
  position: absolute;
  right: 0px;
  margin-right: 2px;
}

/** size of lock icon in treeview vuetify3 **/
.ce-module-toc .v-list-item-title {
  font-size: 0.875rem;
}

.ce-module-toc .mdi-toggle-switch-off {
  color: rgb(var(--v-theme-secondary-darken-1)) !important;
}

.ce-module-toc .mdi-toggle-switch {
  color: rgb(var(--v-theme-primary)) !important;
}

.ce-module-toc .v-btn__content .v-icon {
  color: rgba(0, 0, 0, 0.54) !important;
}

.v-icon .accent--text {
  color: rgb(var(--v-theme-accent)) !important;
}

.text-link {
  cursor: pointer;
  color: rgb(var(--v-theme-accent));
  font-size: 0.875rem;
}
</style>

<style lang="scss">
.imsuperselected {
  background-color: #f4f5f6 !important; /* $mg-05-base from @complispace/cs-design-system */
}
</style>
